import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

const ARGENTINA_ID = 11;

/* Provincias y ciudades desde nuestro propio backend (/countries/:id/states y
 * /states/:id/cities), la misma fuente que usa la edición de eventos.
 *
 * `provSeleccionada` es el id numérico de la provincia; `provNombre` es su
 * nombre, que es lo que sirve para armar la dirección del mapa. */
const useUbicaciones = (paisId = ARGENTINA_ID) => {
    const [provincias, setProvincias] = useState([]);
    const [ciudades, setCiudades] = useState([]);
    const [provSeleccionada, setProvSeleccionada] = useState('');
    const [ciudadSeleccionada, setCiudadSeleccionada] = useState('');
    const [cargandoProvincias, setCargandoProvincias] = useState(true);

    useEffect(() => {
        let cancelado = false;

        const fetchProvincias = async () => {
            setCargandoProvincias(true);
            try {
                const response = await api.mundo.getStatesByCountry(paisId);
                if (cancelado) return;
                if (response.status === 'success') {
                    setProvincias([...response.data].sort((a, b) => a.name.localeCompare(b.name)));
                } else {
                    console.error('Error al obtener provincias:', response.message);
                    setProvincias([]);
                }
            } catch (error) {
                if (!cancelado) {
                    console.error('Error al obtener provincias:', error);
                    setProvincias([]);
                }
            } finally {
                if (!cancelado) setCargandoProvincias(false);
            }
        };

        fetchProvincias();
        return () => { cancelado = true; };
    }, [paisId]);

    useEffect(() => {
        if (!provSeleccionada) {
            setCiudades([]);
            return;
        }

        let cancelado = false;

        const fetchCiudades = async () => {
            try {
                const response = await api.mundo.getCitiesByStates(provSeleccionada);
                if (cancelado) return;
                if (response.status === 'success') {
                    setCiudades([...response.data].sort((a, b) => a.name.localeCompare(b.name)));
                } else {
                    console.error('Error al obtener ciudades:', response.message);
                    setCiudades([]);
                }
            } catch (error) {
                if (!cancelado) {
                    console.error('Error al obtener ciudades:', error);
                    setCiudades([]);
                }
            }
        };

        fetchCiudades();
        return () => { cancelado = true; };
    }, [provSeleccionada]);

    /* La edición de un evento sólo conoce el nombre de la ciudad guardada.
     * Para poder poblar el select de ciudades hay que deducir su provincia. */
    const resolverProvinciaDeCiudad = useCallback(async (nombreCiudad) => {
        if (!nombreCiudad) return;
        try {
            const response = await api.mundo.findCityByName(nombreCiudad, paisId);
            if (response.status === 'success' && response.data?.state_id) {
                setProvSeleccionada(String(response.data.state_id));
            }
        } catch (error) {
            console.error('Error al resolver la provincia de la ciudad:', error);
        }
    }, [paisId]);

    const provNombre = provincias.find((p) => String(p.id) === String(provSeleccionada))?.name ?? '';

    return {
        provincias,
        ciudades,
        provSeleccionada,
        setProvSeleccionada,
        provNombre,
        ciudadSeleccionada,
        setCiudadSeleccionada,
        cargandoProvincias,
        resolverProvinciaDeCiudad,
    };
};

export default useUbicaciones;
